import type { DemPoExtractRow } from '../types/dem';

/** PRO*3 / institutional PO customer codes, e.g. 201A002B, 303E009B */
const PRO3_ITEM_CODE_RE = /\b(\d{3}[A-Z]\d{3}[A-Z]?)\b/gi;

const UOM_TOKENS = new Set(['PKT', 'CTN', 'PCS', 'BOX', 'SET', 'ROLL', 'BAG', 'EA', 'UNIT']);

/** SAP PO Item/Pos like 00010 — S/N only, not a product SKU. */
function isSapLineItemNumber(value: string): boolean {
  return /^0\d{4}$/.test(String(value ?? '').trim());
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Spec/size tags like [ST-STPA6W] — not BTH/customer SKUs. */
function isBracketSpecTag(value: string): boolean {
  return /^\[[^\]]+\]$/.test(value.trim());
}

function isPro3ItemCode(value: string): boolean {
  return /^\d{3}[A-Z]\d{3}[A-Z]?$/i.test(value.trim());
}

function isStandaloneCustomerCode(value: string): boolean {
  const code = value.trim();
  if (!code || code.length < 4 || code.length > 24) return false;
  if (/\s/.test(code)) return false;
  if (isBracketSpecTag(code) || /[\[\]]/.test(code)) return false;
  if (UOM_TOKENS.has(code.toUpperCase())) return false;
  if (/^[\d$.,]+$/.test(code)) return false;
  return /[A-Za-z]/.test(code) && /\d/.test(code);
}

function findPro3Codes(text: string): string[] {
  return [...text.matchAll(PRO3_ITEM_CODE_RE)].map((match) => match[1]!.toUpperCase());
}

function pickFromMultiline(text: string): string | null {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) return null;

  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const line = lines[index]!;
    if (isPro3ItemCode(line)) return line.toUpperCase();
    if (isStandaloneCustomerCode(line)) return line.toUpperCase();
  }
  return null;
}

function pickCustomerItemCode(items: string, description: string): string | null {
  const itemsTrim = String(items ?? '').trim();
  const descTrim = String(description ?? '').trim();

  if (itemsTrim && isBracketSpecTag(itemsTrim)) {
    // fall through — try description / other sources
  } else if (itemsTrim && isPro3ItemCode(itemsTrim)) {
    return itemsTrim.toUpperCase();
  } else if (itemsTrim && isStandaloneCustomerCode(itemsTrim) && !itemsTrim.includes(' ')) {
    return itemsTrim.toUpperCase();
  }

  for (const source of [itemsTrim, descTrim, `${itemsTrim}\n${descTrim}`]) {
    if (!source) continue;
    const fromLines = pickFromMultiline(source);
    if (fromLines) return fromLines;
  }

  const combined = `${itemsTrim} ${descTrim}`.trim();
  const trailing = combined.match(/\b(\d{3}[A-Z]\d{3}[A-Z]?)\s*$/i);
  if (trailing) return trailing[1]!.toUpperCase();

  const pro3Codes = findPro3Codes(combined);
  if (pro3Codes.length > 0) return pro3Codes[pro3Codes.length - 1]!;

  return null;
}

function stripCodeFromText(text: string, code: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';

  const lines = trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length > 1 && lines[lines.length - 1]!.toUpperCase() === code.toUpperCase()) {
    return lines.slice(0, -1).join(' ').trim();
  }

  const withoutTrailing = trimmed
    .replace(new RegExp(`\\s*${escapeRegExp(code)}\\s*$`, 'i'), '')
    .trim();
  return withoutTrailing || trimmed;
}

function buildDescriptionWithoutCode(items: string, description: string, code: string): string {
  const descTrim = String(description ?? '').trim();
  const itemsTrim = String(items ?? '').trim();

  if (descTrim && descTrim.toUpperCase() !== code.toUpperCase()) {
    const cleaned = stripCodeFromText(descTrim, code);
    if (cleaned && cleaned.toUpperCase() !== code.toUpperCase()) return cleaned;
  }

  if (itemsTrim && itemsTrim.toUpperCase() !== code.toUpperCase()) {
    const cleaned = stripCodeFromText(itemsTrim, code);
    if (cleaned && cleaned.toUpperCase() !== code.toUpperCase()) return cleaned;
  }

  return descTrim;
}

function shouldRefineRow(row: DemPoExtractRow): boolean {
  const items = String(row.items ?? '').trim();
  const description = String(row.description ?? '').trim();
  if (!items && !description) return false;
  if (items.includes('\n') || description.includes('\n')) return true;
  if (items.length > 24 || description.length > 24) return true;
  if (items.includes(' ') && !isPro3ItemCode(items) && !isStandaloneCustomerCode(items)) return true;
  return findPro3Codes(`${items} ${description}`).length > 0;
}

export function refinePoExtractRow(row: DemPoExtractRow): DemPoExtractRow {
  const rawItems = String(row.items ?? '').trim();
  // SAP PO Item/Pos (00010…) is S/N — never keep as product SKU.
  const itemsIn =
    isBracketSpecTag(rawItems) || isSapLineItemNumber(rawItems) ? '' : rawItems;
  const descriptionIn = String(row.description ?? '').trim();

  if (!shouldRefineRow({ ...row, items: itemsIn })) {
    if (isPro3ItemCode(itemsIn)) {
      return { ...row, items: itemsIn.toUpperCase() };
    }
    // Cleared SAP S/N (00010) stays blank — do not substitute description as SKU.
    return {
      ...row,
      items: itemsIn,
      description: descriptionIn || itemsIn,
      quantity: String(row.quantity ?? ''),
      uom: String(row.uom ?? ''),
    };
  }

  const extracted = pickCustomerItemCode(itemsIn, row.description);
  if (!extracted) {
    return {
      ...row,
      items: itemsIn,
      description: descriptionIn || itemsIn,
      quantity: String(row.quantity ?? ''),
      uom: String(row.uom ?? ''),
    };
  }

  const description = buildDescriptionWithoutCode(itemsIn, row.description, extracted);
  return {
    ...row,
    items: extracted,
    description:
      description ||
      descriptionIn ||
      itemsIn,
    quantity: String(row.quantity ?? ''),
    uom: String(row.uom ?? ''),
  };
}
